import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { User, Key, ShieldCheck } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfilePage = () => {
  const { user, fetchMe } = useAuth();
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // 2FA state
  const [twoFASecret, setTwoFASecret] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  const [enabling2FA, setEnabling2FA] = useState(false);
  const [disabling2FA, setDisabling2FA] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleEnable2FA = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/enable-2fa`);
      setTwoFASecret(data);
    } catch (e) {
      toast.error('Failed to enable 2FA');
    }
  };

  const handleVerify2FA = async () => {
    setEnabling2FA(true);
    try {
      await axios.post(`${API_URL}/api/auth/verify-2fa`, { code: totpCode });
      toast.success('2FA enabled successfully');
      setTwoFASecret(null);
      setTotpCode('');
      fetchMe();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    } finally {
      setEnabling2FA(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!totpCode) {
      toast.error('Please enter your 2FA code');
      return;
    }
    setDisabling2FA(true);
    try {
      await axios.post(`${API_URL}/api/auth/disable-2fa`, { code: totpCode });
      toast.success('2FA disabled');
      setTotpCode('');
      fetchMe();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    } finally {
      setDisabling2FA(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold">Profile</h1>
        <p className="text-muted-foreground">Manage your account settings</p>
      </div>

      {/* Profile Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <User size={20} className="text-primary" />
            Account Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Name</Label>
              <p className="font-medium">{user?.name}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Email</Label>
              <p className="font-medium">{user?.email}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Role</Label>
              <Badge variant="outline">{user?.role?.replace('_', ' ')}</Badge>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">2FA Status</Label>
              <Badge variant={user?.two_factor_enabled ? 'default' : 'secondary'}>
                {user?.two_factor_enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Key size={20} className="text-primary" />
            Change Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label>Current Password</Label>
              <Input
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                required
                data-testid="current-password"
              />
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                required
                minLength={6}
                data-testid="new-password"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                required
                data-testid="confirm-password"
              />
            </div>
            <Button type="submit" disabled={changingPassword} data-testid="change-password-btn">
              {changingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Two-Factor Authentication */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ShieldCheck size={20} className="text-primary" />
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Add an extra layer of security to your account
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!user?.two_factor_enabled ? (
            twoFASecret ? (
              <div className="space-y-4">
                <div className="p-4 bg-secondary rounded-md">
                  <p className="text-sm mb-2">
                    Scan this QR code with your authenticator app or enter the secret manually:
                  </p>
                  <div className="p-4 bg-white rounded-md inline-block">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(twoFASecret.provisioning_uri)}`}
                      alt="2FA QR Code"
                    />
                  </div>
                  <p className="font-mono text-xs mt-2 break-all">
                    Secret: {twoFASecret.secret}
                  </p>
                </div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1 space-y-2">
                    <Label>Enter 6-digit code</Label>
                    <Input
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value)}
                      placeholder="000000"
                      maxLength={6}
                      data-testid="totp-verify"
                    />
                  </div>
                  <Button onClick={handleVerify2FA} disabled={enabling2FA} data-testid="verify-2fa-btn">
                    {enabling2FA ? 'Verifying...' : 'Verify & Enable'}
                  </Button>
                </div>
              </div>
            ) : (
              <Button onClick={handleEnable2FA} data-testid="enable-2fa-btn">
                Enable Two-Factor Authentication
              </Button>
            )
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Two-factor authentication is currently enabled. Enter your 2FA code to disable it.
              </p>
              <div className="flex gap-2 items-end">
                <div className="flex-1 space-y-2">
                  <Label>Enter 6-digit code</Label>
                  <Input
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    placeholder="000000"
                    maxLength={6}
                    data-testid="totp-disable"
                  />
                </div>
                <Button variant="destructive" onClick={handleDisable2FA} disabled={disabling2FA} data-testid="disable-2fa-btn">
                  {disabling2FA ? 'Disabling...' : 'Disable 2FA'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfilePage;
